import { useEffect, useState } from "react";

import { isElectron } from "./env";
import { isMacPlatform } from "./lib/utils";

export const COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS =
  "[[data-sidebar-state=collapsed]_&]:pl-[var(--workspace-titlebar-content-left)]";

/**
 * Horizontal room the macOS traffic lights need at the left of whichever strip
 * owns the titlebar zone: the window's `trafficLightPosition` plus the
 * controls' width plus breathing space.
 */
export const MACOS_TRAFFIC_LIGHTS_LEFT_INSET = "90px";

/**
 * Whether the window shows macOS traffic lights in its titlebar zone. Pure so
 * the rule is testable: Electron on macOS, and not fullscreen, where macOS
 * hides the controls and an inset would be dead space.
 */
export function resolveMacosTrafficLights(input: {
  readonly isElectron: boolean;
  readonly platform: string;
  readonly isWindowFullscreen: boolean;
}): boolean {
  return input.isElectron && isMacPlatform(input.platform) && !input.isWindowFullscreen;
}

/** Server-side and unit-test renders have no window; the bridge is then simply absent. */
function readDesktopBridge(): Window["desktopBridge"] | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge;
}

/**
 * Live {@link resolveMacosTrafficLights} for titlebar strips, tracking the
 * desktop bridge's fullscreen state so the inset disappears when the controls
 * do.
 */
export function useMacosTrafficLights(): boolean {
  const isMacosDesktop =
    isElectron && typeof navigator !== "undefined" && isMacPlatform(navigator.platform);
  const [isWindowFullscreen, setIsWindowFullscreen] = useState(() => {
    const getWindowFullscreenState = readDesktopBridge()?.getWindowFullscreenState;
    return isMacosDesktop && typeof getWindowFullscreenState === "function"
      ? getWindowFullscreenState()
      : false;
  });

  useEffect(() => {
    if (!isMacosDesktop) return;
    const bridge = readDesktopBridge();
    if (!bridge) return;
    const { getWindowFullscreenState, onWindowFullscreenStateChange } = bridge;
    if (
      typeof getWindowFullscreenState !== "function" ||
      typeof onWindowFullscreenStateChange !== "function"
    ) {
      return;
    }

    const unsubscribe = onWindowFullscreenStateChange(setIsWindowFullscreen);
    setIsWindowFullscreen(getWindowFullscreenState());
    return unsubscribe;
  }, [isMacosDesktop]);

  return isMacosDesktop && !isWindowFullscreen;
}
